"use client"

import { useState, useRef, useEffect } from "react"
import SearchIcon from "@icons/Search.svg?react"
import Link from "next/link"
import clsx from "clsx/lite"

export default ({
    iconColor = "fill-sticks-and-stones",
    className = "",
    onChange = () => {},
    onSubmit = () => {},
}) => {
    const [results, setResults] = useState([])
    const [isOpen, setOpen] = useState(false)
    const inputRef = useRef(null)
    const searchRef = useRef(null)

    useEffect(() => {
        loadPagefind()
        addListeners()
        return removeListeners()
    }, [])

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    const addListeners = () => {
        window.addEventListener("keydown", handleEscape)
        document.addEventListener("mousedown", handleClickOutside)
    }

    const removeListeners = () => {
        window.removeEventListener("keydown", handleEscape)
        window.removeEventListener("mousedown", handleClickOutside)
    }

    const loadPagefind = async () => {
        if (typeof window.pagefind === "undefined") {
            try {
                window.pagefind = await import(
                    /* webpackIgnore: true */ `${process.env.NEXT_PUBLIC_STATIC_PREFIX}/_pagefind/pagefind.js`
                )
            } catch (e) {
                window.pagefind = { debouncedSearch: () => ({ results: [] }) }
            }
        }
    }

    const handleClickOutside = (e) => {
        if (searchRef.current && !searchRef.current.contains(e.target)) {
            close()
        }
    }

    const handleEscape = (e) => {
        if (isOpen && e.key === "Escape") {
            close()
        }
    }

    const handleKeydown = async (e) => {
        if (inputRef.current) {
            const keyword = inputRef.current.value

            await handleSearch(keyword)

            if (e.key === "Enter") {
                onSubmit(keyword)
                close()
                return
            }

            if (e.key === "Escape") {
                close()
                return
            }

            onChange(keyword)

            if (keyword.length === 0) {
                setResults([])
            }
        }
    }

    const handleSearch = async (keywords) => {
        if (window.pagefind) {
            const search = await window.pagefind.debouncedSearch(keywords)
            if (search?.results) {
                const results = await Promise.all(
                    search.results.map(async (result) => ({
                        id: result.id,
                        ...(await result.data()),
                    })),
                )
                setResults(results.filter(({ url }) => url.indexOf("_") === -1))
            }
        }
    }

    function toggle() {
        isOpen ? close() : open()
    }

    function open() {
        setOpen(true)
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    function close() {
        setOpen(false)
        setResults([])
        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }

    return (
        <div
            ref={searchRef}
            className={clsx(
                "w-full flex items-center h-[44px] absolute right-0",
                className,
                isOpen ? "z-40" : "z-10",
            )}
        >
            <div className="shrink flex justify-center items-center absolute right-0 z-20">
                <SearchIcon
                    onClick={toggle}
                    className={clsx(
                        "relative cursor-pointer transition-all right-0",
                        isOpen ? "fill-arugula right-4" : iconColor,
                    )}
                />
            </div>
            <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                className={clsx(
                    "outline-none text-caviar transition-all duration-300 p-3 pl-5 absolute right-0",
                    isOpen ?
                        "opacity-100 bg-white-snow w-full"
                    :   "w-0 opacity-0 pointer-events-none bg-transparent",
                )}
                onKeyDown={handleKeydown}
            />
            <div
                className={clsx(
                    "md:w-auto w-full md:max-w-[calc(100%-2.5rem)] top-full mt-1 absolute p-4 pt-3 duration-200 ease-out z-50 transition-all h-auto overflow-hidden bg-white-snow shadow-xl text-caviar md:mx-5 border-t border-arugula",
                    isOpen && results.length > 0 ?
                        "opacity-100 scale-100"
                    :   "opacity-0 scale-90",
                )}
            >
                {results.slice(0, 5).map((result) => {
                    const { id, url, meta, content } = result
                    return (
                        <Link
                            key={id}
                            href={url
                                .replace(".html", "")
                                .replace("/static/", "/")}
                            className="block px-3.5 py-3 text-base border-l-4 border-l-white-snow hover:border-l-twilight-gray"
                        >
                            <strong className="block mb-1 font-bold text-caviar text-xl truncate w-full">
                                {meta.title}
                            </strong>
                            <span className="block font-medium leading-5 opacity-90 text-caviar truncate w-full">
                                {content.slice(0, 128)}
                            </span>
                        </Link>
                    )
                })}
            </div>
        </div>
    )
}
